import type { FC } from 'react';
import React from 'react';
import {
  createFeature,
  useLogFrontendApiError,
  useTranslation,
  withErrorBoundary,
} from '@edreamsodigeo/frontend-core';
import { Body, Box, Flex, Pill } from '@edreamsodigeo/prisma-design-system';

import useIsPrimeUserLogged from '@/clientState/hooks/useIsPrimeUserLogged';
import { useMetaFlowProvider } from '@/clientState/providers/MetaFlowProvider';
import { SkeletonContinueSection } from '@/components/FlightSummary/MapItinerary/components/SkeletonMapItinerary';
import ContinueButton from '@/components/FlightSummaryMeta/components/ContinueButton';
import Price from '@/components/FlightSummaryMeta/components/Price';
import type { ItineraryPriceSummary } from '@/generated/graphql';
import { useFlightSummarySelectedTotalPrice } from '@/tracking/useFlightSummarySelectedTotalPrice';
import {
  EQUAL_PROMINENCE_MOBILE_META,
  PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE,
} from '@/versionedComponents';
import type { FlightSummaryMetaFooterProps } from './config/types';
import { PrimeDaysWrapper } from '@/components/FlightSummaryMeta/components/PrimeDaysBanner/PrimeDaysBanner';
import usePrimeSiteProperties from '@/queries/useprimeSiteProperties';

const PrimeDaysPillComponent = () => {
  const { t } = useTranslation();
  return (
    <PrimeDaysWrapper>
      <Box py="1x_small">
        <Pill type="promotional" rounded>
          <Body type="xSmall">{t('meta.promobanner.pill')}</Body>
        </Pill>
      </Box>
    </PrimeDaysWrapper>
  );
};

export const PrimeDaysPill = createFeature({
  name: PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE,
  variations: [null, PrimeDaysPillComponent],
  version: 1,
});

const FlightSummaryMetaFooterComponent: FC<
  FlightSummaryMetaFooterProps
> = () => {
  const priceResult = useFlightSummarySelectedTotalPrice();
  const { data, loading } = priceResult;
  const summary = data?.getSummaryPrice as ItineraryPriceSummary;
  const price = summary?.nonDiscountedPricePerPassenger;

  useLogFrontendApiError(priceResult);

  return (
    <>
      {loading ? (
        <SkeletonContinueSection />
      ) : (
        <Box width="100%" px="base">
          <PrimeDaysPill />
          <Flex
            justifyContent="space-between"
            flexDirection="column"
            alignItems="center"
          >
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              width="100%"
              flex={1}
            >
              {price && <Price price={price} />}
            </Flex>
            <Flex flexDirection="row" width="100%" my="base" flex={1}>
              <ContinueButton />
            </Flex>
          </Flex>
        </Box>
      )}
    </>
  );
};

const PrimeEqualProminenceFooterComponent: FC<FlightSummaryMetaFooterProps> = ({
  primeEqualProminenceMobile,
}) => {
  const isMeta = useMetaFlowProvider();
  const { isPrimeUserLogged, loadingUserAccountInfo } = useIsPrimeUserLogged();
  const { data: primeSitesResponse, loading: loadingPrimeSite } =
    usePrimeSiteProperties();
  const isPrimeSite =
    primeSitesResponse?.primeSiteProperties?.isPrimeMarket ?? false;

  if (loadingUserAccountInfo || loadingPrimeSite) {
    return <SkeletonContinueSection />;
  }
  if (!isPrimeSite) {
    return <FlightSummaryMetaFooterComponent />;
  }

  if (!isPrimeUserLogged && isMeta && primeEqualProminenceMobile) {
    return primeEqualProminenceMobile;
  }

  return <FlightSummaryMetaFooterComponent />;
};

const FlightSummaryMetaFooterVersionedComponent = createFeature({
  name: EQUAL_PROMINENCE_MOBILE_META,
  variations: [
    FlightSummaryMetaFooterComponent,
    PrimeEqualProminenceFooterComponent,
  ],
  version: 1,
});

const FlightSummaryMetaFooter = withErrorBoundary(
  FlightSummaryMetaFooterVersionedComponent,
  {
    name: 'FlightSummaryMetaFooter',
  }
);

export default FlightSummaryMetaFooter;
